import React, { useState, useEffect, useRef } from 'react';
import { any, bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';
import BasicDropdown from '../../BasicDropdown/BasicDropdown';
import { findConfigForSelectFilter } from '../../../util/search';
import config from '../../../config';

import IconPlusSetting from '../TopbarMobileMenu/IconPlus/IconPlusSettings';
import IconPlusInbox from '../TopbarMobileMenu/IconPlus/IconPlusInbox';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    currentTab,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    setGateImage,
    isTransparent,
    relative,
    darkBackground,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [showShopMenu, setShowShopMenu] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);

  const [isSettingsOpen, setIsSettingsOpen] = useState(ACCOUNT_SETTINGS_PAGES.includes(currentPage));
  const [isInboxOpen, setIsInboxOpen] = useState(currentPage === 'InboxPage' && currentTab);

  const handleShowShopMenuChange = showShopMenu => setShowShopMenu(showShopMenu);
  const handleShowSearchBarChange = () => setShowSearchBar(true);

  const searchRef = useRef(null);
  const handleHideSearchBarChange = event => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowSearchBar(false);
      console.log("click outside");
    }
  };

  useEffect(() => {
    setMounted(true);

    if (showSearchBar) {
      document.addEventListener('click', handleHideSearchBarChange);
    } else {
      document.removeEventListener('click', handleHideSearchBarChange);
    }
    return () => {
      document.removeEventListener('click', handleHideSearchBarChange);
    };
  }, [showSearchBar]);


  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  let defaultRootClass = css.root;

  if (relative) {
    defaultRootClass = css.relativeRoot;
  }

  if (isTransparent) {
    defaultRootClass = css.rootTransparent;
  }

  const classes = classNames(rootClassName || defaultRootClass, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.createListingLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const userAbbreviatedName = currentUser?.attributes?.profile?.abbreviatedName;
  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.profileAvatarWrapper}>{userAbbreviatedName}</div>
        {/* <Avatar className={css.avatar} user={currentUser} disableProfileLink /> */}
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            {'My listings'}
          </NamedLink>
        </MenuItem>

        <MenuItem key="FavListingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('FavListingsPage'))}
            name="FavListingsPage"
          >
            <FormattedMessage id="TopbarDesktop.FavListingsPage" />
            <span className={css.menuItemBorder} />
          </NamedLink>
        </MenuItem>

        <MenuItem key="NewListingPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('NewListingPage'))}
            name="NewListingPage"
          >
            {'List an item'}
            <span className={css.menuItemBorder} />
          </NamedLink>
        </MenuItem>

        <MenuItem key="OrdersPage">
          <NamedLink
            className={classNames(css.yourListingsLink, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'orders' })}
            name="InboxPage" params={{ tab: 'orders' }}
          >
            <FormattedMessage id="InboxPage.ordersTabTitle" />
            <span className={css.menuItemBorder} />
          </NamedLink>
        </MenuItem>
        <MenuItem key="SalesPage">
          <NamedLink
            className={classNames(css.yourListingsLink, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'sales' })}
            name="InboxPage" params={{ tab: 'sales' }}
          >
            <FormattedMessage id="InboxPage.salesTabTitle" />
            <span className={css.menuItemBorder} />
          </NamedLink>
        </MenuItem>

        {/* <MenuItem key="InboxPage">
          <div className={css.dropdownButton} onClick={() => setIsInboxOpen(!isInboxOpen)}>
            <IconPlusInbox isInboxOpen={isInboxOpen} />
            <span className={css.yourListingsLink}>
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            </span>
          </div>

          <div
            className={classNames(css.dropdown, { [css.isOpen]: isInboxOpen })}
          >
            <NamedLink
              className={classNames(css.yourListingsLink, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'orders' })}
              name="InboxPage" params={{ tab: 'orders' }}
            >
              <FormattedMessage id="InboxPage.ordersTabTitle" />
              <span className={css.menuItemBorder} />
            </NamedLink>
            <NamedLink
              className={classNames(css.yourListingsLink, { [css.currentPage]: currentPage === 'InboxPage' && currentTab === 'sales' })}
              name="InboxPage" params={{ tab: 'sales' }}
            >
              <FormattedMessage id="InboxPage.salesTabTitle" />
              <span className={css.menuItemBorder} />
            </NamedLink>
          </div>
        </MenuItem> */}

        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            <span className={css.menuItemBorder} />
          </NamedLink>
        </MenuItem>

        <MenuItem key="AccountSettingsPage">
          <div className={css.dropdownButton} onClick={() => setIsSettingsOpen(!isSettingsOpen)}>
            <IconPlusSetting isSettingsOpen={isSettingsOpen} />
            <span className={css.yourListingsLink}>
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </span>
          </div>
          <div
            className={classNames(css.dropdown, { [css.isOpen]: isSettingsOpen })}
          >
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('ContactDetailsPage'))}
              name="ContactDetailsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.contactDetailsLink" />
              <span className={css.menuItemBorder} />
            </NamedLink>
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('PasswordChangePage'))}
              name="PasswordChangePage">
              <FormattedMessage id="TopbarMobileMenu.passwordLink" />
              <span className={css.menuItemBorder} />
            </NamedLink>
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('PaymentMethodsPage'))}
              name="PaymentMethodsPage">
              <FormattedMessage id="TopbarMobileMenu.payoutDetailsLink" />
              <span className={css.menuItemBorder} />
            </NamedLink>
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('StripePayoutPage'))}
              name="StripePayoutPage">
              <FormattedMessage id="TopbarMobileMenu.paymentMethodsLink" />
              <span className={css.menuItemBorder} />
            </NamedLink>
          </div>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const productTypeConfig = findConfigForSelectFilter('productType', config.custom.filters);
  const productTypes = productTypeConfig.options ? productTypeConfig.options : [];

  const productTypeCategoriesValues = productTypes
    .map(i => i.category)
    .sort((a, b) => a.localeCompare(b));
  const productTypeCategories = Array.from(new Set(productTypeCategoriesValues));

  const brandConfig = findConfigForSelectFilter('brand', config.custom.filters);
  const brands = brandConfig.options ? brandConfig.options : [];
  const brandCategories = ['A – F', 'G – M', 'N – T', 'U – Z'];

  const findProductImage = productLabel => {
    switch (productLabel) {
      case 'ProductPic':
        return 'productPic';
      case 'Art':
        return 'artPic';
      case 'Beds':
        return 'bedsPic';
      case 'Chairs':
        return 'chairsPic';
      case 'Couches':
        return 'sofaPic';
      case 'Interiors':
        return 'interiorsPic';
      case 'Lighting':
        return 'lightingPic';
      case 'Outdoor':
        return 'outdoorFurniturePic';
      case 'Storage':
        return 'storagePic';
      case 'Tables':
        return 'tablePic';
      case 'Tableware':
        return 'tablewarePic';
    }
  };
  const shopMenu = (
    <>
      <BasicDropdown
        contentClassName={css.contentWrapper}
        onShowChange={handleShowShopMenuChange}
        wrapperClassName={css.basicDropdownWrapper}
        triggerButton={
          <span className={css.createListingLink}>
            <span className={css.createListing}>Shop</span>
          </span>
        }
        hideOnScroll={true}
        menuContent={
          <div className={css.shopMenuContent}>
            <div className={css.shopMenuColumn}>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=' }}
                className={css.shopMenuColumnHead}
              >
                By room
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:bathroom' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('bathroomPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Bathroom
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:bedroom' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('bedroomPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Bedroom
              </NamedLink>

              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:dining' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('diningPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Dining
              </NamedLink>

              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:hallway' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('hallwayPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Hallway
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:kitchen' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('kitchenPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Kitchen
              </NamedLink>

              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:laundry' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('laundryPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Laundry
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:living' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('livingPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Living
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:nursery' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('nurseryPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Nursery
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:office' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('officePic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Office
              </NamedLink>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_room=has_any:outdoor' }}
                className={css.shopMenuColumnItem}
                onMouseEnter={() => {
                  if (setGateImage) {
                    setGateImage('outdoorPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                Outdoor
              </NamedLink>
            </div>
            <div className={css.shopMenuColumn}>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: '?pub_productType=' }}
                className={css.shopMenuColumnHead}
                onMouseEnter={() => {
                  console.log('setGateImage(\'productPic\');');
                  if (setGateImage) {
                    setGateImage('productPic');
                  }
                }}
                onMouseLeave={() => {
                  if (setGateImage) {
                    setGateImage('couchTexture');
                  }
                }}
              >
                By product
              </NamedLink>
              {productTypeCategories.map((pc, index) => {
                let searchString = '';
                productTypes.forEach(p => {
                  if (p.category === pc) {
                    searchString += `${p.key},`;
                  }
                });

                searchString = searchString.slice(0, -1);
                return (
                  <NamedLink
                    key={index}
                    name="SearchPageWithCategories"
                    to={{ search: `?pub_productType=has_any:${searchString}` }}
                    className={css.shopMenuColumnItem}
                    onMouseEnter={() => {
                      if (setGateImage) {
                        setGateImage(findProductImage(pc));
                      }
                    }}
                    onMouseLeave={() => {
                      if (setGateImage) {
                        setGateImage('couchTexture');
                      }
                    }}
                  >
                    {pc}
                  </NamedLink>
                );
              })}
            </div>
            <div className={css.shopMenuColumn}>
              <NamedLink
                name="SearchPageWithCategories"
                to={{ search: `?pub_category=designer` }}
                className={css.shopMenuColumnHead}
              >
                By brand
              </NamedLink>
              {brandCategories.map((bc, index) => {
                let searchString = '';
                brands.forEach(b => {
                  if (b.category === bc) {
                    searchString += `${b.key},`;
                  }
                });
                searchString = searchString.slice(0, -1);
                return (
                  <NamedLink
                    key={index}
                    name="SearchPageWithCategories"
                    to={{ search: `?category=${bc}&pub_brand=has_any:` }}
                    className={css.shopMenuColumnItem}
                  >
                    {bc}
                  </NamedLink>
                );
              })}
              <NamedLink
                name="SearchPage"
                to={{ search: `?pub_category=vintage` }}
                className={css.shopMenuColumnHead}
              >
                By vintage
              </NamedLink>
            </div>
            <div className={css.dummyShopMenuColumn} />
          </div>
        }
        arch={true}
      />
    </>
  );

  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <Logo
          format="desktop"
          className={css.logoContent}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
          darkBackground={darkBackground}
        />
      </NamedLink>
      <div ref={searchRef} className={css.rootRightSideColored}>
        {shopMenu}
        <NamedLink className={css.createListingLink} name="NewListingPage">
          <span className={css.createListing}>Sell</span>
        </NamedLink>

        {/* {inboxLink} */}

        {!showSearchBar && (
          <div className={css.searchWrapper} onClick={handleShowSearchBarChange}>
            <span className={css.createListing}>Search</span>
          </div>
        )}

        {showSearchBar && <>{search}</>}

        {!isAuthenticatedOrJustHydrated && (
          <NamedLink className={css.createListingLink} style={{ width: 110, flex: 'none', justifyContent: "center" }} name="NewListingPage">
            <span className={css.createListing}>Rotate Room</span>
          </NamedLink>
        )}

        {authenticatedOnClientSide && (
          <div className={css.profileMenuWrapper}>{profileMenu}</div>
        )}

      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
